<template>
  <div v-if="effect" :key="`effect-${effect?.id}`" class="effect-menu">
    <item-header
      type="effect"
      :label="$t('labels.effectOn')"
      @close="$emit('close')"
    >
      <store-item
        :id="effect.affectedId"
        v-slot="{ item }"
        :type="effect.affectedType"
        tag="span"
      >
        {{ item.title }}
      </store-item>
    </item-header>

    <drawer-block
      v-model:open="drawers.effectOverRounds"
      :title="$t('labels.effectOverRounds', [$t(`roundUnits.${roundUnit}`, 10)])"
    >
      <transition-group-list tag="div" class="list mt-1">
        <div class="effect-menu__line" :class="{ '-special': expressionsSpecial?.[0] }">
          <span class="effect-menu__linelabel label">
            {{ $t("labels.immediateEffect") }}{{ $t(":") }}
          </span>
          <expression-input
            :expression="expressions[0] ?? ''"
            :parse-context="{
              affectedType: effect.affectedType,
              affectedId: effect.affectedId
            }"
            default-sign="+"
            @update:expression="updateImpulseExpression(0, $event)"
          />
        </div>

        <div class="effect-menu__line">
          <span class="label">{{ $t("labels.after") }}...</span>
        </div>

        <form
          v-for="(expression, index) in [...expressions.slice(1), null]"
          :key="state.roundExpressionKeys[index + 1]"
          class="effect-menu__line"
          :class="{ '-special': expressionsSpecial[index + 1] }"
          @submit.prevent="onEffectLineSubmit($event as SubmitEvent, index + 1)"
        >
          <span class="effect-menu__linelabel label">
            {{ index + 1 }}
            {{ $t(`roundUnits.${roundUnit}`, index + 1) }}{{ $t(":") }}
          </span>
          <expression-input
            ref="expressionInputs"
            :expression="expression ?? state.newExpression"
            :parse-context="{
              affectedType: effect.affectedType,
              affectedId: effect.affectedId
            }"
            default-sign="+"
            @update:expression="updateImpulseExpression(index + 1, $event)"
          />

          <button
            v-if="index + 1 < expressions.length"
            type="button"
            :title="$t('actions.delete')"
            class="button-icon p-1 button-icon_small self-stretch"
            @click="deleteEffectRound(index + 1)"
          >
            <lucide-icon name="trash-2" />
          </button>

          <button
            v-else
            ref="addButton"
            type="submit"
            class="button-icon p-1 button-icon_small self-stretch"
          >
            <lucide-icon name="plus" />
          </button>
        </form>
      </transition-group-list>
    </drawer-block>

    <drawer-block
      v-model:open="drawers.effectVisibility"
      :title="$t('labels.effectVisibility')"
    >
      <div class="column">
        <input-radio
          :value="effect.visibility"
          :data="EFFECT_VISIBILITY.visible"
          :label="$t('labels.effectVisible')"
          @update:value="ctx.updateKey('visibility', $event)"
        />
        <input-radio
          :value="effect.visibility"
          :data="EFFECT_VISIBILITY.hidden"
          :label="$t('labels.effectHidden')"
          @update:value="ctx.updateKey('visibility', $event)"
        />
        <input-radio
          :value="effect.visibility"
          :data="EFFECT_VISIBILITY.hint"
          :label="$t('labels.effectHinted')"
          @update:value="ctx.updateKey('visibility', $event)"
        />
      </div>

      <div
        v-if="effect.visibility === EFFECT_VISIBILITY.hint"
        class="pl-6 mt-2"
      >
        <input-text
          :value="effect.hint"
          :label="$t('labels.hint')"
          @update:value="ctx.updateKey('hint', $event)"
        />
      </div>

      <h3 class="label">{{ $t("headings.pendingEffectsVisibility") }}</h3>
      <div class="column mt-2">
        <input-checkbox
          :value="effect.pendingEffectsVisible"
          :label="$t('labels.pendingEffectsVisible')"
          @update:value="ctx.updateKey('pendingEffectsVisible', $event)"
        />
        <input-checkbox
          :value="effect.pendingEffectsOpenable"
          :label="$t('labels.pendingEffectsOpenable')"
          @update:value="ctx.updateKey('pendingEffectsOpenable', $event)"
        />
      </div>
    </drawer-block>

    <menu-group>
      <button
        class="button button_secondary mt-4"
        type="button"
        @click="deleteEffect()"
      >
        {{ $t("actions.deleteEffect") }}
      </button>
    </menu-group>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, watch, nextTick, ref } from "vue";

import MenuGroup from "@core/ui/MenuGroup.vue";
import StoreItem from "@editor/items/StoreItem.vue";
import DrawerBlock from "@core/ui/DrawerBlock.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";
import ItemHeader from "@editor/components/ItemHeader.vue";
import TransitionGroupList from "@core/wrappers/TransitionGroupList.vue";

import InputRadio from "@core/input/RadioButton.vue";
import InputCheckbox from "@core/input/InputCheckbox.vue";
import InputText from "@core/input/InputText.vue";
import ExpressionInput from "@editor/items/expression/ExpressionInput.vue";

import { useEffectStore } from "@editor/items/effect/effects.store";
import { useSettingsStore } from "@editor/store/settings.store";
import { useDrawerStates } from "@editor/store/editor/drawerStates.store";
import { useEffect } from "./useEffect";

const props = defineProps<{
  effectId: number;
}>();

const emit = defineEmits<{
  (event: "close"): void;
}>();

const state = reactive({
  roundExpressionKeys: [] as number[],
  newExpression: ""
});

const ctx = useEffect(() => props.effectId);

const addButton = ref<HTMLButtonElement | null>(null);

const drawers = useDrawerStates();

const roundUnit = computed(() => {
  return useSettingsStore().simulationRoundUnit;
});

const effect = computed(() => {
  return useEffectStore().items[props.effectId];
});

const expressions = computed((): string[] => {
  return effect.value?.impulseExpressions ?? [];
});
const expressionsSpecial = computed((): boolean[] => {
  return expressions.value.map((expression) => {
    return expression.length > 10 || expression.includes("{");
  });
});

watch(() => effect.value.id, () => {
  state.roundExpressionKeys = [];
  for (let i = 0; i <= expressions.value.length; i++) {
    state.roundExpressionKeys[i] = Math.random();
  }
}, { immediate: true });

function updateImpulseExpression(index: number, value: string) {
  const impulseExpressions = expressions.value.slice();

  // create local keys if needed
  while (index >= state.roundExpressionKeys.length - 1) {
    state.roundExpressionKeys.push(Math.random());
  }

  impulseExpressions[index] = value;
  useEffectStore().update({
    id: effect.value.id,
    impulseExpressions
  });
}
function onEffectLineSubmit(event: SubmitEvent, index: number) {
  if (index < expressions.value.length) {
    return;
  }
  updateImpulseExpression(index, state.newExpression);
  if (event.submitter?.tagName === "BUTTON") {
    nextTick(() => {
      addButton.value?.focus?.();
    });
  }
}

function deleteEffectRound(index: number) {
  const expressions = effect.value.impulseExpressions.slice();
  expressions.splice(index, 1);
  state.roundExpressionKeys.splice(index, 1);
  useEffectStore().update({
    id: effect.value.id,
    impulseExpressions: expressions
  });
}

function deleteEffect() {
  emit("close");
  const effectStore = useEffectStore();
  effectStore.delete({
    id: effect.value.id
  });
}

const EFFECT_VISIBILITY = {
  visible: "visible" as const,
  hidden: "hidden" as const,
  hint: "hint" as const
};
</script>

<style lang="scss">
.effect-menu {
  &__line {
    display: grid;
    grid-template-columns: 8rem 1fr 2rem;
    gap: 0.5rem;
    align-items: center;

    &.-special {
      grid-template-columns: 1fr 2rem;
    }
  }

  &__linelabel {
    white-space: nowrap;
    align-self: start;
    margin-top: 0.75rem;
  }
  &__line.-special &__linelabel {
    grid-column: span 2;
    margin-top: 0;
  }
}
</style>
